import { useEffect, useRef, useState } from "react";
import { CheckCircle2, XCircle, AlertTriangle, Plus, Trash2, Link2, Loader2, Gauge, Wand2, Sparkles, History } from "lucide-react";
import { fieldsFor } from "../../config/contentSeo";
import api, { formatApiError } from "../../lib/api";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { toast } from "sonner";
import RichTextEditor from "../admin/RichTextEditor";
import FaqManager from "./FaqManager";
import SeoScore from "./SeoScore";
import RichContent from "../public/RichContent";
import InternalLinkManager from "./InternalLinkManager";
import RelatedContentManager from "./RelatedContentManager";
import VersionHistory from "./VersionHistory";
import DraftPublishBar from "./DraftPublishBar";

const stripText = (html) => (html || "").replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();

function KeywordCheck({ label, ok }) {
  return (
    <div className="flex items-center gap-2 text-xs" data-testid={`kw-check-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
      {ok ? <CheckCircle2 className="h-4 w-4 text-green-500" /> : <XCircle className="h-4 w-4 text-white/25" />}
      <span className={ok ? "text-white/80" : "text-white/45"}>{label}</span>
    </div>
  );
}

export default function ContentSeoSection({ entityType = "product", value, onChange, meta = {}, entity = {}, onRestored }) {
  const seo = value || {};
  const content = seo.content || {};
  const keywords = seo.keywords || { primary: "", secondary: [], related: [] };
  const fields = fieldsFor(entityType);

  const set = (patch) => onChange({ ...seo, ...patch });
  const setContent = (key, html) => set({ content: { ...content, [key]: html } });
  const setKw = (patch) => set({ keywords: { ...keywords, ...patch } });

  const applyTemplateContent = (rendered) => {
    const mergedContent = { ...content };
    Object.entries(rendered.content || {}).forEach(([k, v]) => { if (v) mergedContent[k] = v; });
    const patch = { content: mergedContent };
    if ((rendered.faqs || []).length) patch.faqs = [...(seo.faqs || []), ...rendered.faqs];
    set(patch);
    toast.success("Template applied — review and save/publish.");
  };

  // ---- AI Content Assistant (2C): preview + approve, never auto-overwrite ----
  const [ai, setAi] = useState(null); // {field, label, existing, proposed, faqs}
  const [aiLoading, setAiLoading] = useState(false);
  const runAi = async (field, label) => {
    setAiLoading(true);
    try {
      const { data } = await api.post("/admin/seo/generate-content", { entity_type: entityType, entity: { ...entity, name: entity.name || meta.h1 }, field, seo });
      if (!data.ok) { toast.error(data.error || "Generation failed."); return; }
      if (field === "faqs") setAi({ field, label, faqs: data.faqs || [] });
      else setAi({ field, label, existing: content[field] || (field === "meta_description" ? "" : ""), proposed: data.html ?? data.value ?? "", isHtml: !!data.html });
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setAiLoading(false); }
  };
  const acceptAi = () => {
    if (ai.field === "faqs") {
      set({ faqs: [...(seo.faqs || []), ...ai.faqs], ai_generated: { ...(seo.ai_generated || {}), faqs: true } });
    } else if (ai.field === "meta_description") {
      set({ meta_description: ai.proposed, auto: false, ai_generated: { ...(seo.ai_generated || {}), meta_description: true } });
    } else {
      set({ content: { ...content, [ai.field]: ai.proposed }, ai_generated: { ...(seo.ai_generated || {}), [ai.field]: true } });
    }
    toast.success("AI content applied. Review and edit before saving.");
    setAi(null);
  };
  const AiBtn = ({ field, label }) => (
    <Button type="button" size="sm" variant="outline" className="gap-1 h-7 border-gold/40 text-gold hover:bg-gold/10" onClick={() => runAi(field, label)} disabled={aiLoading} data-testid={`ai-generate-${field}`}>
      {aiLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Wand2 className="h-3 w-3" />} AI
    </Button>
  );


  // ---- Focus keyword placement analysis (client-side, quick) ----
  const kw = (keywords.primary || "").trim().toLowerCase();
  const allContent = fields.map((f) => stripText(content[f.key])).join(" ").toLowerCase();
  const firstPara = stripText(content.main || content.intro || "").toLowerCase().split(". ")[0] || "";
  const subheads = (Object.values(content).join(" ").match(/<h[2-6][^>]*>(.*?)<\/h[2-6]>/gi) || []).join(" ").toLowerCase();
  const imgAlts = (Object.values(content).join(" ").match(/alt="([^"]*)"/gi) || []).join(" ").toLowerCase();
  const has = (hay) => kw && hay.includes(kw);
  const checks = kw ? [
    { label: "SEO Title", ok: has((meta.title || "").toLowerCase()) },
    { label: "Meta Description", ok: has((meta.meta_description || "").toLowerCase()) },
    { label: "URL Slug", ok: has((meta.slug || "").toLowerCase().replace(/-/g, " ")) },
    { label: "H1 Heading", ok: has((meta.h1 || "").toLowerCase()) },
    { label: "First Paragraph", ok: has(firstPara) },
    { label: "A Subheading", ok: has(subheads) },
    { label: "Main Content", ok: has(allContent) },
    { label: "Image ALT Text", ok: has(imgAlts) },
  ] : [];

  const refs = seo.external_refs || [];
  const links = seo.internal_links || [];
  const setRefs = (r) => set({ external_refs: r });
  const setLinks = (l) => set({ internal_links: l });
  const uid = () => Math.random().toString(36).slice(2, 10);
  const versionEntityId = entityType === "page" ? (entity.key || entity.slug) : entity[`${entityType}_id`];
  const [showVersions, setShowVersions] = useState(false);

  // ---- Live Content SEO analysis (2B) ----
  const [analysis, setAnalysis] = useState(null);
  const [analysing, setAnalysing] = useState(false);
  const timer = useRef(null);
  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      setAnalysing(true);
      api.post("/admin/seo/content-analysis", { entity_type: entityType, entity: { name: meta.h1 }, seo, meta })
        .then(({ data }) => setAnalysis(data)).catch(() => {}).finally(() => setAnalysing(false));
    }, 700);
    return () => timer.current && clearTimeout(timer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(seo.content), JSON.stringify(seo.faqs), JSON.stringify(seo.keywords), JSON.stringify(seo.internal_links), JSON.stringify(seo.external_refs), entityType]);

  const statusIcon = (s) => s === "pass" ? <CheckCircle2 className="h-4 w-4 text-green-500 shrink-0" /> : s === "warn" ? <AlertTriangle className="h-4 w-4 text-yellow-500 shrink-0" /> : <XCircle className="h-4 w-4 text-red-500 shrink-0" />;

  return (
    <div className="space-y-8" data-testid="content-seo-section">
      {/* Header actions */}
      <div className="flex items-center justify-end gap-2 -mb-4">
        <ApplyTemplate entityType={entityType} entity={entity} seo={seo} onApply={applyTemplateContent} />
        <Button type="button" variant="outline" size="sm" className="gap-2" onClick={() => setShowVersions(true)} disabled={!versionEntityId} data-testid="open-version-history">
          <History className="h-4 w-4" /> Version History
        </Button>
      </div>
      {versionEntityId && (
        <VersionHistory entityType={entityType} entityId={versionEntityId} open={showVersions} onOpenChange={setShowVersions} onRestored={onRestored || (() => window.location.reload())} />
      )}

      {/* Draft / Publish workflow */}
      {versionEntityId && (
        <DraftPublishBar entityType={entityType} entityId={versionEntityId} entity={entity} seo={seo} onReload={onRestored || (() => window.location.reload())} />
      )}
      {/* Content SEO Score */}
      <div className="rounded-lg border border-white/10 bg-white/5 p-4" data-testid="content-seo-score">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-4">
            {analysis ? <SeoScore score={analysis.score} /> : <Gauge className="h-10 w-10 text-white/30" />}
            <div>
              <p className="text-sm font-medium flex items-center gap-2">Content SEO Score {analysing && <Loader2 className="h-3 w-3 animate-spin text-white/40" />}</p>
              <p className="text-xs text-white/50">Separate from the Technical SEO score. Updates as you edit.</p>
            </div>
          </div>
          {analysis?.metrics && (
            <div className="flex flex-wrap gap-x-5 gap-y-1 text-[11px] text-white/60">
              <span data-testid="metric-words">{analysis.metrics.word_count} words</span>
              <span>{analysis.metrics.headings} headings</span>
              <span>{analysis.metrics.internal_links} internal links</span>
              <span>{analysis.metrics.images_with_alt}/{analysis.metrics.images} img ALT</span>
              <span>{analysis.metrics.faqs} FAQs</span>
              <span data-testid="metric-readability">Flesch {analysis.metrics.readability.flesch} · {analysis.metrics.readability.interpretation}</span>
            </div>
          )}
        </div>
        {analysis?.checks && (
          <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1.5 mt-4">
            {analysis.checks.map((c) => (
              <div key={c.id} className="flex items-start gap-2 text-xs" data-testid={`content-check-${c.id}`}>
                {statusIcon(c.status)}
                <div><span className="text-white/80 font-medium">{c.label}</span>{c.message && <span className="text-white/50"> — {c.message}</span>}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Focus keywords */}
      <div className="rounded-lg border border-white/10 bg-white/5 p-4">
        <p className="text-sm font-medium mb-3">Focus Keywords</p>
        <div className="grid md:grid-cols-3 gap-3">
          <div><Label className="text-xs uppercase tracking-wider text-white/60">Primary Keyword</Label><Input value={keywords.primary || ""} onChange={(e) => setKw({ primary: e.target.value })} data-testid="kw-primary" placeholder="e.g. 32 shot barrage" /></div>
          <div><Label className="text-xs uppercase tracking-wider text-white/60">Secondary (comma-separated)</Label><Input value={(keywords.secondary || []).join(", ")} onChange={(e) => setKw({ secondary: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })} data-testid="kw-secondary" /></div>
          <div><Label className="text-xs uppercase tracking-wider text-white/60">Related (comma-separated)</Label><Input value={(keywords.related || []).join(", ")} onChange={(e) => setKw({ related: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })} data-testid="kw-related" /></div>
        </div>
        {kw && (
          <div className="mt-4">
            <p className="text-xs text-white/50 mb-2">Primary keyword placement (avoid stuffing — natural usage is best):</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">{checks.map((c) => <KeywordCheck key={c.label} {...c} />)}</div>
          </div>
        )}
      </div>

      {/* Content fields */}
      {fields.map((f) => (
        <div key={f.key} data-testid={`content-field-${f.key}`}>
          <div className="flex items-center justify-between mb-2">
            <Label className="text-sm font-medium text-white/85">{f.label}{seo.ai_generated?.[f.key] && <Sparkles className="inline h-3 w-3 text-gold ml-1" title="AI-generated" />}</Label>
            <div className="flex items-center gap-2">
              {f.safety && <span className="text-[11px] text-amber-400">Verified info only.</span>}
              <AiBtn field={f.key} label={f.label} />
            </div>
          </div>
          <RichTextEditor value={content[f.key] || ""} onChange={(html) => setContent(f.key, html)} />
        </div>
      ))}

      {/* FAQs */}
      <div>
        <div className="flex items-center justify-between">
          <Label className="text-sm font-medium text-white/85">Frequently Asked Questions</Label>
          <AiBtn field="faqs" label="FAQs" />
        </div>
        <p className="text-xs text-white/50 mb-3">Enabled FAQs appear in an accordion on the page and generate FAQPage structured data.</p>
        <FaqManager value={seo.faqs || []} onChange={(faqs) => set({ faqs })} />
      </div>

      {/* Internal links (Phase 2D: stable entity refs + intelligent suggestions) */}
      <InternalLinkManager
        entityType={entityType}
        entityId={entity[`${entityType}_id`] || (entityType === "page" ? entity.slug : undefined)}
        links={links}
        onChange={setLinks}
      />

      {/* Related content (manual pins + auto) */}
      <div className="space-y-3" data-testid="related-content-managers">
        <Label className="text-sm font-medium text-white/85 flex items-center gap-2">Related Content</Label>
        {entityType === "product" && (
          <RelatedContentManager entityType={entityType} entityId={entity.product_id} kind="products" value={seo.related_content || {}} onChange={(rc) => set({ related_content: rc })} />
        )}
        <RelatedContentManager entityType={entityType} entityId={entity[`${entityType}_id`] || entity.slug} kind="categories" value={seo.related_content || {}} onChange={(rc) => set({ related_content: rc })} />
        <RelatedContentManager entityType={entityType} entityId={entity[`${entityType}_id`] || entity.slug} kind="brands" value={seo.related_content || {}} onChange={(rc) => set({ related_content: rc })} />
      </div>

      {/* External references */}
      <div>
        <Label className="text-sm font-medium text-white/85">External References</Label>
        <p className="text-xs text-white/50 mb-3">Approved external sources. Only shown publicly when enabled.</p>
        <div className="space-y-3">
          {refs.map((r, i) => (
            <div key={r.id || i} className="rounded-lg border border-white/10 bg-white/5 p-3 space-y-2" data-testid={`external-ref-row-${i}`}>
              <div className="flex gap-2">
                <Input value={r.title || ""} onChange={(e) => setRefs(refs.map((x, j) => (j === i ? { ...x, title: e.target.value } : x)))} placeholder="Title" className="flex-1 bg-black/30 border-white/15" />
                <Input value={r.url || ""} onChange={(e) => setRefs(refs.map((x, j) => (j === i ? { ...x, url: e.target.value } : x)))} placeholder="https://…" className="flex-1 bg-black/30 border-white/15" />
                <Button type="button" variant="ghost" size="icon" className="text-red-400" onClick={() => setRefs(refs.filter((_, j) => j !== i))}><Trash2 className="h-4 w-4" /></Button>
              </div>
              <Input value={r.description || ""} onChange={(e) => setRefs(refs.map((x, j) => (j === i ? { ...x, description: e.target.value } : x)))} placeholder="Optional description" className="bg-black/30 border-white/15" />
              <div className="flex flex-wrap gap-4 text-xs">
                <label className="flex items-center gap-2"><Switch checked={r.enabled !== false ? !!r.enabled : false} onCheckedChange={(v) => setRefs(refs.map((x, j) => (j === i ? { ...x, enabled: v } : x)))} data-testid={`ext-ref-enable-${i}`} /> Show publicly</label>
                <label className="flex items-center gap-2"><Switch checked={r.new_tab !== false} onCheckedChange={(v) => setRefs(refs.map((x, j) => (j === i ? { ...x, new_tab: v } : x)))} /> New tab</label>
                <label className="flex items-center gap-2"><Switch checked={r.nofollow !== false} onCheckedChange={(v) => setRefs(refs.map((x, j) => (j === i ? { ...x, nofollow: v } : x)))} /> Nofollow</label>
                <label className="flex items-center gap-2"><Switch checked={!!r.sponsored} onCheckedChange={(v) => setRefs(refs.map((x, j) => (j === i ? { ...x, sponsored: v } : x)))} /> Sponsored</label>
              </div>
            </div>
          ))}
          <Button type="button" variant="outline" size="sm" className="gap-1" onClick={() => setRefs([...refs, { id: uid(), title: "", url: "", description: "", new_tab: true, nofollow: true, sponsored: false, enabled: false }])} data-testid="external-ref-add"><Plus className="h-4 w-4" /> Add reference</Button>
        </div>
      </div>

      {/* AI approval dialog */}
      <Dialog open={!!ai} onOpenChange={(o) => !o && setAi(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="ai-content-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Sparkles className="h-5 w-5 text-gold" /> AI: {ai?.label}</DialogTitle>
            <DialogDescription>Review the AI-generated content below. It is generated from verified data only and is never saved automatically — approve to insert, then edit as needed before saving the page.</DialogDescription>
          </DialogHeader>
          {ai && ai.field === "faqs" && (
            <div className="space-y-3" data-testid="ai-faq-preview">
              {ai.faqs.length === 0 ? <p className="text-white/50 text-sm">No FAQs generated.</p> : ai.faqs.map((f, i) => (
                <div key={i} className="rounded-lg border border-white/10 p-3">
                  <p className="text-white/90 font-medium text-sm">{f.question}</p>
                  <RichContent html={f.answer} className="text-sm mt-1" />
                </div>
              ))}
              <p className="text-[11px] text-white/40">These will be appended to your existing FAQs (existing ones are kept).</p>
            </div>
          )}
          {ai && ai.field !== "faqs" && (
            <div className="space-y-4">
              {ai.existing && stripText(ai.existing) && (
                <div>
                  <p className="text-[11px] uppercase tracking-wider text-white/40 mb-1">Current content (will be replaced)</p>
                  <div className="rounded-lg border border-white/10 bg-black/30 p-3 max-h-40 overflow-y-auto"><RichContent html={ai.isHtml ? ai.existing : `<p>${ai.existing}</p>`} className="text-sm" /></div>
                </div>
              )}
              <div>
                <p className="text-[11px] uppercase tracking-wider text-gold mb-1">Proposed (editable)</p>
                <Textarea rows={8} value={ai.proposed} onChange={(e) => setAi({ ...ai, proposed: e.target.value })} className="bg-black/30 border-white/15 font-mono text-xs" data-testid="ai-proposed-text" />
                <div className="mt-2 rounded-lg border border-white/10 bg-black/20 p-3"><RichContent html={ai.isHtml ? ai.proposed : `<p>${ai.proposed}</p>`} className="text-sm" /></div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setAi(null)}>Cancel</Button>
            <Button className="cf-gold-btn" onClick={acceptAi} data-testid="ai-approve-btn">Approve & Insert</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


function ApplyTemplate({ entityType, entity, seo, onApply }) {
  const [templates, setTemplates] = useState([]);
  const [open, setOpen] = useState(false);
  const [confirm, setConfirm] = useState(null); // {rendered, overwrites}
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/admin/seo/templates", { params: { entity_type: entityType } })
      .then(({ data }) => setTemplates(data.templates || [])).catch(() => {});
  }, [entityType]);

  const choose = async (t) => {
    setOpen(false); setBusy(true);
    try {
      const { data } = await api.post(`/admin/seo/templates/${t.template_id}/render`,
        { entity: { name: entity.name || entity.title, seo } });
      if (!data.ok) { toast.error(data.error || "Render failed."); return; }
      const content = seo.content || {};
      const overwrites = Object.keys(data.content || {}).filter((k) => data.content[k] && (content[k] || "").trim());
      setConfirm({ rendered: data, overwrites, name: t.name });
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (templates.length === 0) return null;
  return (
    <>
      <div className="relative">
        <Button type="button" variant="outline" size="sm" className="gap-2" onClick={() => setOpen((v) => !v)} disabled={busy} data-testid="apply-template-btn">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Apply Template
        </Button>
        {open && (
          <div className="absolute right-0 z-20 mt-1 w-64 rounded-md border border-white/10 bg-neutral-900 shadow-xl divide-y divide-white/5" data-testid="apply-template-list">
            {templates.map((t) => (
              <button key={t.template_id} type="button" onClick={() => choose(t)} className="w-full text-left px-3 py-2 hover:bg-white/5 text-sm text-white/85 flex items-center justify-between" data-testid={`apply-template-${t.template_id}`}>
                {t.name} {t.is_default && <span className="text-[10px] text-gold">default</span>}
              </button>
            ))}
          </div>
        )}
      </div>
      <Dialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <DialogContent data-testid="apply-template-confirm">
          <DialogHeader>
            <DialogTitle>Apply “{confirm?.name}”?</DialogTitle>
            <DialogDescription>
              {confirm?.overwrites.length
                ? `This will OVERWRITE existing content in: ${confirm.overwrites.join(", ")}. Other empty sections will be filled.`
                : "This will fill empty content sections. No existing content will be overwritten."}
              {" "}You can still edit before saving/publishing.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirm(null)}>Cancel</Button>
            <Button className="cf-gold-btn" onClick={() => { onApply(confirm.rendered); setConfirm(null); }} data-testid="apply-template-confirm-btn">Apply</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
